<div class="webhooks-page">
  <ysh-page-header title="Webhooks" tag="Beta">
    <a [routerLink]="docsRoute">
      <ysh-button label="Documentation" icon="menu_book" theme="smoke" size="small"/>
    </a>
  </ysh-page-header>

  @if (loading) {
    <ysh-panel><div class="webhooks-page__loading"></div></ysh-panel>
  } @else if (!authorized) {
    <ysh-panel heading="Webhooks" padding="all-sides">
      <p class="empty-state">
        Webhook settings are available to company owners and admins only.
      </p>
    </ysh-panel>
  } @else if (notProvisioned) {
    <ysh-panel padding="all-sides">
      <div class="empty-state empty-state--centered">
        <mat-icon class="empty-state__icon">cloud_off</mat-icon>
        <h3 class="empty-state__title">No integration yet</h3>
        <p class="empty-state__text">
          A webhook integration hasn't been provisioned for your company yet.
          Reach out to our team and we'll get one set up — then you can manage it right here.
        </p>
        <a class="empty-state__cta" href="mailto:support@yoshimobility.com?subject=Webhook integration request">
          <ysh-button label="Contact Support" icon="mail" theme="blue"/>
        </a>
      </div>
    </ysh-panel>
  } @else if (integration) {
    <div class="panels">
      <!-- Configuration -->
      <ysh-panel class="panel" heading="Configuration" padding="all-sides">
        <div class="config">
          <dl class="details">
            <div><dt>Name</dt><dd>{{ integration.name || '—' }}</dd></div>
            <div><dt>Auth Type</dt><dd>{{ integration.authType }}</dd></div>
            <div><dt>Delivery Type</dt><dd>{{ integration.deliveryType }}</dd></div>
          </dl>

          <hr class="panel-divider">

          <div class="config__row config__row--toggle">
            <span class="config__label">{{ enabled ? 'Enabled' : 'Disabled' }}</span>
            <mat-slide-toggle
              [checked]="enabled"
              (change)="handleToggleEnabled($event.checked)"
            ></mat-slide-toggle>
          </div>

          <mat-form-field class="config__field">
            <mat-label>Webhook URL</mat-label>
            <input matInput type="url" [(ngModel)]="webhookUrl" placeholder="https://example.com/yoshi/webhooks">
          </mat-form-field>

          <div class="config__row">
            <div class="config__label">Event Types</div>
          </div>
          <div class="config__events">
            @for (eventType of allEventTypes; track eventType) {
              <mat-checkbox
                [checked]="isEventTypeSelected(eventType)"
                (change)="toggleEventType(eventType, $event.checked)"
              >{{ eventType }}</mat-checkbox>
            }
          </div>

          <div class="config__actions">
            <ysh-button
              label="Save Changes"
              theme="blue"
              size="small"
              [loading]="saving"
              [disabled]="saving || !hasUnsavedChanges"
              (click)="handleSave()"
            />
            <ysh-button
              label="Send Test"
              icon="bolt"
              theme="smoke"
              size="small"
              [loading]="testing"
              [disabled]="testing"
              (click)="handleSendTest()"
            />
          </div>

          @if (testResult) {
            <div class="test-result" [class.test-result--ok]="testResult.status >= 200 && testResult.status < 300">
              <div class="test-result__status">Test response: HTTP {{ testResult.status }}</div>
              <pre class="test-result__body">{{ testResult.body }}</pre>
            </div>
          }
        </div>
      </ysh-panel>

      <!-- Signing secret -->
      <ysh-panel class="panel" heading="Signing Secret" padding="all-sides">
        <div class="secret">
          @if (revealedSecret) {
            <div class="secret__warning">
              <mat-icon size-medium>warning</mat-icon>
              Copy this secret now — you won't be able to see it again.
            </div>
            <div class="secret__reveal">
              <code class="secret__value">{{ revealedSecret }}</code>
              <ysh-button icon="content_copy" label="Copy" theme="blue" size="small" (click)="copySecret()"/>
            </div>
            @if (rotatedPreviousSecretUntil) {
              <div class="secret__banner">
                Your previous secret remains valid until {{ rotatedPreviousSecretUntil }}.
                Both signatures are sent during the grace period.
              </div>
            }
            <a class="secret__dismiss" (click)="dismissRevealedSecret()">I've saved it</a>
          } @else {
            <div class="secret__hint">
              <div class="config__label">Current secret</div>
              <code class="secret__masked">{{ integration.signingSecretHint || '—' }}</code>
            </div>
            @if (integration.hasPreviousSecretActive) {
              <div class="secret__banner">
                A previously rotated secret is still valid until {{ integration.previousSecretActiveUntilFormatted }}.
              </div>
            }
            <ysh-button
              label="Rotate Secret"
              icon="autorenew"
              theme="smoke"
              size="small"
              [loading]="rotating"
              [disabled]="rotating"
              (click)="handleRotateSecret()"
            />
          }
        </div>

        <div class="signature-note">
          <h4>Verifying signatures</h4>
          <p>
            Yoshi signs each request with HMAC-SHA256 over
            <code>{{ '{X-Yoshi-Timestamp}.{raw_body}' }}</code> and sends the headers
            <code>X-Yoshi-Event-Id</code>, <code>X-Yoshi-Event-Type</code>,
            <code>X-Yoshi-Idempotency-Key</code>, <code>X-Yoshi-Timestamp</code>, and
            <code>X-Yoshi-Signature</code>.
          </p>
          <p>
            During secret rotation the signature header may contain multiple comma-separated
            signatures (<code>sha256=…,sha256=…</code>) — treat the request as valid if any one
            matches your secret. Use <code>X-Yoshi-Idempotency-Key</code> to deduplicate events.
          </p>
          <p>
            <a class="signature-note__link" [routerLink]="docsRoute">Read the full webhook documentation →</a>
          </p>
        </div>
      </ysh-panel>
    </div>

    <!-- Deliveries -->
    <section class="deliveries">
      <div class="deliveries__head">
        <h3>Event Deliveries</h3>
      </div>
      <ysh-table-set
        [dataSource]="deliveriesDataSource"
        (onItemTap)="handleOpenDelivery($event)"
      />
    </section>
  }
</div>
